if (!customElements.get('gm-highlighted-bundles')) {
  customElements.define(
    'gm-highlighted-bundles',
    class GmHighlightedBundles extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabList = this.querySelector('[role="tablist"]');
        this.removeAttribute('title');
        this.viewport = this.querySelector('.gm-highlighted-bundles__viewport');
        this.stage = this.querySelector('.gm-highlighted-bundles__stage');
        this.inner = this.querySelector('.gm-highlighted-bundles__inner');
        this.sectionTitleElement = this.querySelector('.gm-highlighted-bundles__title');
        this.frame = this.querySelector('.gm-highlighted-bundles__frame');
        this.galleryColumn = this.querySelector('.gm-highlighted-bundles__gallery-column');
        this.infoColumn = this.querySelector('.gm-highlighted-bundles__info-column');
        this.infoPanel = this.querySelector('.gm-highlighted-bundles__info-panel');
        this.infoContent = this.querySelector('.gm-highlighted-bundles__info-content');
        this.currentHandle = this.querySelector('[data-gm-bundle-part="panel"].is-current')?.dataset.gmBundleHandle || '';
        this.switchTimer = null;
        this.frames = new Set();
        this.measureFrame = 0;
        this.scrollFrame = 0;
        this.scrollAttached = false;
        this.pendingClamp = false;
        this.layout = '';
        this.travel = 0;
        this.stageOffset = 0;
        this.viewportHeight = 0;
        this.sceneHeight = 0;
        this.lastProgress = null;
        this.measureSignature = '';
        this.pointerStates = new Map();
        this.suppressedPointerClicks = new Map();
        this.dragClickSuppressionMs = 1000;
        this.videoStates = new Map();
        this.mediaObserver = null;

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.scheduleMeasure = this.scheduleMeasure.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.renderScroll = this.renderScroll.bind(this);
        this.onMediaPointerDown = this.onMediaPointerDown.bind(this);
        this.onMediaPointerMove = this.onMediaPointerMove.bind(this);
        this.onMediaPointerEnd = this.onMediaPointerEnd.bind(this);
        this.onMediaLinkClick = this.onMediaLinkClick.bind(this);
        this.onMotionChange = this.onMotionChange.bind(this);
        this.onMediaProximity = this.onMediaProximity.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onTabKeydown);
        });

        this.sliders = Array.from(this.querySelectorAll('slider-component'));
        this.sliders.forEach((slider) => slider.addEventListener('slideChanged', this.onSlideChanged));

        this.addEventListener('pointerdown', this.onMediaPointerDown, true);
        this.addEventListener('pointermove', this.onMediaPointerMove, true);
        this.addEventListener('pointerup', this.onMediaPointerEnd, true);
        this.addEventListener('pointercancel', this.onMediaPointerEnd, true);
        this.addEventListener('click', this.onMediaLinkClick, true);

        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktopQuery.addEventListener('change', this.scheduleMeasure);
        this.motionQuery.addEventListener('change', this.onMotionChange);
        window.addEventListener('resize', this.scheduleMeasure);

        if ('IntersectionObserver' in window) {
          this.mediaObserver = new IntersectionObserver(this.onMediaProximity, { rootMargin: '100% 0px' });
          this.mediaObserver.observe(this);
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
          [this.galleryColumn, this.infoContent, this.sectionTitleElement, this.frame].forEach((element) => {
            if (element) this.resizeObserver.observe(element);
          });
        }

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => {
            if (this.isConnected) this.scheduleMeasure();
          });
        }

        if (window.Shopify && Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }

        this.applyUrlState();
        this.measure();
        this.queueFrame(() => {
          this.scrollTabIntoView(this.currentHandle);
          this.resetActiveSlider();
          this.prepareActiveMedia();
          this.syncVideos();
          this.scheduleMeasure();
        });
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
          tab.removeEventListener('keydown', this.onTabKeydown);
        });
        this.sliders.forEach((slider) => slider.removeEventListener('slideChanged', this.onSlideChanged));
        this.removeEventListener('pointerdown', this.onMediaPointerDown, true);
        this.removeEventListener('pointermove', this.onMediaPointerMove, true);
        this.removeEventListener('pointerup', this.onMediaPointerEnd, true);
        this.removeEventListener('pointercancel', this.onMediaPointerEnd, true);
        this.removeEventListener('click', this.onMediaLinkClick, true);
        this.desktopQuery?.removeEventListener('change', this.scheduleMeasure);
        this.motionQuery?.removeEventListener('change', this.onMotionChange);
        window.removeEventListener('resize', this.scheduleMeasure);
        this.unbindScroll();
        this.resizeObserver?.disconnect();
        this.mediaObserver?.disconnect();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.clearTimeout(this.switchTimer);
        this.frames.forEach((id) => window.cancelAnimationFrame(id));
        this.frames.clear();
        this.measureFrame = 0;
        this.pointerStates.clear();
        this.suppressedPointerClicks.forEach((suppression) => window.clearTimeout(suppression.timer));
        this.suppressedPointerClicks.clear();
        this.videoStates.forEach((state, video) => {
          video.removeEventListener('error', state.onError);
          if (state.playingListener) video.removeEventListener('playing', state.playingListener);
          this.cancelVideoReadiness(video, state);
          if (!video.paused) video.pause();
        });
        this.videoStates.clear();
      }

      queueFrame(callback) {
        const id = window.requestAnimationFrame(() => {
          this.frames.delete(id);
          callback();
        });
        this.frames.add(id);
        return id;
      }

      onTabClick(event) {
        this.select(event.currentTarget.dataset.gmBundleHandle, { updateUrl: true, animate: true });
      }

      onTabKeydown(event) {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        const nextTab = this.tabs[nextIndex];
        nextTab.focus();
        this.select(nextTab.dataset.gmBundleHandle, { updateUrl: true, animate: true });
      }

      onSlideChanged(event) {
        if (this.isVisibleOrNearViewport()) {
          this.prepareGalleryMedia(event.currentTarget.closest('[data-gm-bundle-part="gallery"]'));
        }
        this.syncVideos();
      }

      onMotionChange() {
        this.scheduleMeasure();
        this.syncVideos();
      }

      onMediaProximity(entries) {
        const entry = entries.find((item) => item.target === this);
        if (!entry) return;
        if (entry.isIntersecting) this.prepareActiveMedia();
        this.syncVideos();
      }

      onMediaPointerDown(event) {
        if (!event.isPrimary || event.button !== 0) return;
        const link = event.target.closest?.('.gm-highlighted-bundles__media-link');
        if (!link || !this.contains(link)) return;
        this.clearSuppressedPointerClick(event.pointerId);
        this.clearSuppressedLinkClicks(link);
        this.pointerStates.set(event.pointerId, {
          link,
          startX: event.clientX,
          startY: event.clientY,
          dragged: false,
        });
      }

      onMediaPointerMove(event) {
        const state = this.pointerStates.get(event.pointerId);
        if (!state || state.dragged) return;
        const distance = Math.hypot(event.clientX - state.startX, event.clientY - state.startY);
        if (distance > 8) state.dragged = true;
      }

      onMediaPointerEnd(event) {
        const state = this.pointerStates.get(event.pointerId);
        if (!state) return;
        this.pointerStates.delete(event.pointerId);
        if (event.type === 'pointercancel' || !state.dragged) return;

        this.clearSuppressedPointerClick(event.pointerId);
        const timer = window.setTimeout(() => {
          this.suppressedPointerClicks.delete(event.pointerId);
        }, this.dragClickSuppressionMs);
        this.suppressedPointerClicks.set(event.pointerId, { link: state.link, timer });
      }

      onMediaLinkClick(event) {
        const link = event.target.closest?.('.gm-highlighted-bundles__media-link');
        if (!link || !this.contains(link)) return;
        if (
          event.detail === 0 ||
          (Number.isFinite(event.button) && event.button !== 0) ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        const pointerId = Number.isFinite(event.pointerId) ? event.pointerId : null;
        let suppression = pointerId === null ? null : this.suppressedPointerClicks.get(pointerId);

        if (!suppression) {
          suppression = Array.from(this.suppressedPointerClicks.entries()).find((entry) => entry[1].link === link);
          if (suppression) suppression = { pointerId: suppression[0], ...suppression[1] };
        } else {
          suppression = { pointerId, ...suppression };
        }

        if (!suppression || suppression.link !== link) return;
        this.clearSuppressedPointerClick(suppression.pointerId);
        event.preventDefault();
      }

      clearSuppressedPointerClick(pointerId) {
        const suppression = this.suppressedPointerClicks.get(pointerId);
        if (!suppression) return;
        window.clearTimeout(suppression.timer);
        this.suppressedPointerClicks.delete(pointerId);
      }

      clearSuppressedLinkClicks(link) {
        this.suppressedPointerClicks.forEach((suppression, pointerId) => {
          if (suppression.link === link) this.clearSuppressedPointerClick(pointerId);
        });
      }

      onBlockSelect(event) {
        if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
        const block = event.target?.closest?.('[data-gm-bundle-block]');
        if (!block || !this.contains(block)) return;
        this.select(block.dataset.gmBundleHandle, { updateUrl: false, animate: false });
      }

      applyUrlState() {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('gm_bundle')) return;

        const handle = params.get('gm_bundle');
        const known = Array.from(this.querySelectorAll('[data-gm-bundle-part="panel"]')).some(
          (panel) => panel.dataset.gmBundleHandle === handle
        );
        if (known) this.select(handle, { updateUrl: false, animate: false });
      }

      select(handle, options) {
        if (!handle) return;
        window.clearTimeout(this.switchTimer);
        if (handle === this.currentHandle) {
          this.classList.remove('is-fading');
          return;
        }
        const reduceMotion = this.motionQuery?.matches;
        this.prepareGallery(handle);

        const apply = () => {
          this.pendingClamp = true;
          this.releaseFocus(handle);
          this.querySelectorAll('[data-gm-bundle-part]').forEach((part) => {
            const active = part.dataset.gmBundleHandle === handle;
            part.classList.toggle('is-current', active);
            part.toggleAttribute('hidden', !active);
            if (active) {
              part.removeAttribute('inert');
              part.setAttribute('aria-hidden', 'false');
            } else {
              part.setAttribute('inert', '');
              part.setAttribute('aria-hidden', 'true');
            }
          });

          this.tabs.forEach((tab) => {
            const selected = tab.dataset.gmBundleHandle === handle;
            tab.classList.toggle('is-selected', selected);
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.tabIndex = selected ? 0 : -1;
          });

          this.currentHandle = handle;
          if (options.updateUrl) this.writeUrl(handle);
          this.scrollTabIntoView(handle);
          this.queueFrame(() => {
            this.resetActiveSlider();
            this.syncVideos();
            this.scheduleMeasure();
          });
        };

        if (options.animate === false || reduceMotion) {
          this.classList.remove('is-fading');
          apply();
          return;
        }

        this.classList.add('is-fading');
        this.switchTimer = window.setTimeout(() => {
          apply();
          this.queueFrame(() => this.classList.remove('is-fading'));
        }, 200);
      }

      writeUrl(handle) {
        if (window.Shopify && Shopify.designMode) return;
        const url = new URL(window.location.href);
        url.searchParams.set('gm_bundle', handle);
        window.history.replaceState(window.history.state, '', url);
      }

      prepareGallery(handle) {
        if (!this.isVisibleOrNearViewport()) return;
        const gallery = Array.from(this.querySelectorAll('[data-gm-bundle-part="gallery"]')).find(
          (item) => item.dataset.gmBundleHandle === handle
        );
        this.prepareGalleryMedia(gallery, 0);
      }

      prepareActiveMedia() {
        if (!this.isVisibleOrNearViewport()) return;
        const gallery = this.querySelector('[data-gm-bundle-part="gallery"].is-current');
        this.prepareGalleryMedia(gallery);
      }

      prepareGalleryMedia(gallery, preferredIndex) {
        if (!gallery) return;
        const slider = gallery.querySelector('slider-component');
        const slides = Array.from(
          slider?.sliderItemsToShow?.length
            ? slider.sliderItemsToShow
            : gallery.querySelectorAll('.gm-highlighted-bundles__slide')
        );
        if (!slides.length) return;

        const currentIndex = Number.isInteger(preferredIndex)
          ? preferredIndex
          : Math.max(0, (slider?.currentPage || 1) - 1);
        [currentIndex - 1, currentIndex, currentIndex + 1].forEach((index) => {
          const slide = slides[index];
          if (!slide) return;
          slide
            .querySelectorAll('img.gm-highlighted-bundles__media, img.gm-highlighted-bundles__video-poster')
            .forEach((image) => {
              if (!image.complete) image.loading = 'eager';
            });
          if (index === currentIndex) {
            const video = slide.querySelector('video.gm-highlighted-bundles__video');
            if (video?.preload === 'none') video.preload = 'metadata';
          }
        });
      }

      isVisibleOrNearViewport() {
        const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
        if (!viewportHeight) return false;
        const rect = this.getBoundingClientRect();
        return rect.bottom >= -viewportHeight && rect.top <= viewportHeight * 2;
      }

      releaseFocus(nextHandle) {
        const active = document.activeElement;
        if (!active || !this.contains(active)) return;
        const part = active.closest('[data-gm-bundle-part]');
        if (!part || part.dataset.gmBundleHandle === nextHandle) return;
        const tab = this.tabs.find((item) => item.dataset.gmBundleHandle === nextHandle);
        if (tab) tab.focus();
      }

      scrollTabIntoView(handle) {
        if (!this.tabList) return;
        const tab = this.tabs.find((item) => item.dataset.gmBundleHandle === handle);
        if (!tab) return;
        const listRect = this.tabList.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const left =
          this.tabList.scrollLeft + tabRect.left - listRect.left - (this.tabList.clientWidth - tabRect.width) / 2;
        this.tabList.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
      }

      resetActiveSlider() {
        const gallery = this.querySelector('[data-gm-bundle-part="gallery"].is-current');
        const slider = gallery?.querySelector('slider-component');
        if (!slider?.slider) return;
        slider.slider.scrollLeft = 0;
        if (typeof slider.resetPages === 'function') slider.resetPages();
      }

      syncVideos() {
        const reduceMotion = this.motionQuery?.matches;
        const moduleReady = this.isVisibleOrNearViewport();
        this.querySelectorAll('video').forEach((video) => {
          const hidden = video.closest('[hidden]');
          const playable = moduleReady && !hidden && !reduceMotion && this.isCurrentSlide(video);
          const state = this.getVideoState(video);
          if (!playable) {
            if (state.active) {
              state.active = false;
              this.cancelVideoReadiness(video, state);
              this.showVideoPoster(video);
            }
            if (!video.paused) video.pause();
            return;
          }

          if (state.active) return;
          state.active = true;
          this.cancelVideoReadiness(video, state);
          const token = state.token;
          video.muted = true;
          video.setAttribute('playsinline', '');
          if (video.preload === 'none') video.preload = 'metadata';

          if (!video.paused) {
            this.waitForVideoFrame(video, state, token);
            return;
          }

          let pending;
          try {
            pending = video.play();
          } catch (error) {
            this.showVideoPoster(video);
            return;
          }

          if (pending && typeof pending.then === 'function') {
            pending
              .then(() => this.waitForVideoFrame(video, state, token))
              .catch(() => {
                if (state.token !== token) return;
                this.showVideoPoster(video);
              });
          } else {
            this.waitForVideoFrame(video, state, token);
          }
        });
      }

      getVideoState(video) {
        let state = this.videoStates.get(video);
        if (state) return state;
        state = {
          active: false,
          token: 0,
          frameCallback: null,
          animationFrames: [],
          playingListener: null,
          onError: null,
        };
        state.onError = () => {
          this.cancelVideoReadiness(video, state);
          this.showVideoPoster(video);
          if (!video.paused) video.pause();
        };
        video.addEventListener('error', state.onError);
        this.videoStates.set(video, state);
        return state;
      }

      cancelVideoReadiness(video, state) {
        state.token += 1;
        if (state.frameCallback !== null && typeof video.cancelVideoFrameCallback === 'function') {
          video.cancelVideoFrameCallback(state.frameCallback);
        }
        state.frameCallback = null;
        if (state.playingListener) {
          video.removeEventListener('playing', state.playingListener);
          state.playingListener = null;
        }
        state.animationFrames.forEach((id) => {
          window.cancelAnimationFrame(id);
          this.frames.delete(id);
        });
        state.animationFrames = [];
      }

      waitForVideoFrame(video, state, token) {
        if (!this.isConnected || !state.active || state.token !== token) return;
        if (typeof video.requestVideoFrameCallback === 'function') {
          state.frameCallback = video.requestVideoFrameCallback(() => {
            state.frameCallback = null;
            this.hideVideoPoster(video, state, token);
          });
          return;
        }

        const revealAfterPaint = () => {
          const firstFrame = this.queueFrame(() => {
            state.animationFrames = state.animationFrames.filter((id) => id !== firstFrame);
            const secondFrame = this.queueFrame(() => {
              state.animationFrames = state.animationFrames.filter((id) => id !== secondFrame);
              this.hideVideoPoster(video, state, token);
            });
            state.animationFrames.push(secondFrame);
          });
          state.animationFrames.push(firstFrame);
        };

        if (!video.paused && video.readyState >= 2) {
          revealAfterPaint();
          return;
        }

        state.playingListener = () => {
          video.removeEventListener('playing', state.playingListener);
          state.playingListener = null;
          revealAfterPaint();
        };
        video.addEventListener('playing', state.playingListener, { once: true });
      }

      hideVideoPoster(video, state, token) {
        if (
          !this.isConnected ||
          !state.active ||
          state.token !== token ||
          this.motionQuery?.matches ||
          video.paused ||
          video.readyState < 2 ||
          video.closest('[hidden]') ||
          !this.isCurrentSlide(video)
        ) {
          return;
        }
        const poster = video.parentElement?.querySelector('.gm-highlighted-bundles__video-poster');
        if (poster) poster.hidden = true;
      }

      showVideoPoster(video) {
        const poster = video.parentElement?.querySelector('.gm-highlighted-bundles__video-poster');
        if (poster) poster.hidden = false;
      }

      isCurrentSlide(video) {
        const slide = video.closest('[id^="Slide-"]');
        if (!slide) return true;
        const slider = video.closest('slider-component');
        if (slider?.sliderItemsToShow?.length > 1 && slider.currentPage) {
          return slider.sliderItemsToShow[slider.currentPage - 1] === slide;
        }
        return slide.dataset.gmSlideIndex === '1';
      }

      scheduleMeasure() {
        if (this.measureFrame || !this.isConnected) return;
        this.measureFrame = this.queueFrame(() => {
          this.measureFrame = 0;
          this.measure();
        });
      }

      measure() {
        if (!this.viewport || !this.frame || !this.stage || !this.infoPanel || !this.infoContent) return;
        if (!this.desktopQuery.matches) {
          this.commitLayout('stacked');
          return;
        }

        const gallery = this.querySelector('[data-gm-bundle-part="gallery"].is-current');
        const galleryHeight = gallery?.offsetHeight || 0;
        if (!galleryHeight) {
          this.commitLayout('fit');
          return;
        }

        const frameStyles = window.getComputedStyle(this.frame);
        const frameHeight = Math.round(
          galleryHeight +
            (parseFloat(frameStyles.paddingTop) || 0) +
            (parseFloat(frameStyles.paddingBottom) || 0) +
            (parseFloat(frameStyles.borderTopWidth) || 0) +
            (parseFloat(frameStyles.borderBottomWidth) || 0)
        );
        const stageStyles = window.getComputedStyle(this.stage);
        const stagePadTop = parseFloat(stageStyles.paddingTop) || 0;
        const stagePadBottom = parseFloat(stageStyles.paddingBottom) || 0;
        const titleBlock = this.sectionTitleElement
          ? this.sectionTitleElement.offsetHeight +
            (parseFloat(window.getComputedStyle(this.sectionTitleElement).marginBottom) || 0)
          : 0;
        const available = Math.round(window.visualViewport?.height || window.innerHeight);
        const chromeAbove = stagePadTop + titleBlock;
        const stageOffset = Math.round(Math.max(0, (available - frameHeight) / 2 - chromeAbove));
        const projectedSceneHeight = chromeAbove + frameHeight + stagePadBottom;
        const sceneFits = stageOffset + projectedSceneHeight <= available + 0.5;

        if (this.motionQuery.matches || !sceneFits) {
          this.commitLayout('static');
          return;
        }

        if (this.layout !== 'fit' && this.layout !== 'pin') {
          this.dataset.gmLayout = 'fit';
          this.layout = 'fit';
        }
        this.style.setProperty('--gm-hb-frame-height', `${frameHeight}px`);
        this.style.setProperty('--gm-hb-stage-offset', `${stageOffset}px`);
        this.style.setProperty('--gm-hb-viewport-height', `${available}px`);

        const restoringPadding = this.layout !== 'pin';
        if (restoringPadding) {
          this.infoContent.style.setProperty('padding-top', 'var(--layout-space-32)');
          this.infoContent.style.setProperty('padding-bottom', 'var(--layout-space-24)');
        }
        const viewport = this.infoPanel.clientHeight;
        const contentScroll = this.infoContent.scrollHeight;
        if (restoringPadding) {
          this.infoContent.style.removeProperty('padding-top');
          this.infoContent.style.removeProperty('padding-bottom');
        }

        const rawTravel = contentScroll - viewport;
        const travel = !viewport || rawTravel <= 0.5 ? 0 : Math.ceil(rawTravel);
        if (!travel) {
          this.commitLayout('fit', { frameHeight, stageOffset, viewportHeight: available });
          return;
        }

        const sceneHeight = this.stage.offsetHeight;
        this.commitLayout('pin', { frameHeight, travel, stageOffset, sceneHeight, viewportHeight: available });
      }

      commitLayout(layout, metrics = {}) {
        const frameHeight = metrics.frameHeight || 0;
        const travel = metrics.travel || 0;
        const stageOffset = metrics.stageOffset || 0;
        const sceneHeight = metrics.sceneHeight || 0;
        const viewportHeight = metrics.viewportHeight || 0;
        const signature = [layout, frameHeight, travel, stageOffset, sceneHeight, viewportHeight].join(':');
        if (signature === this.measureSignature) {
          if (this.pendingClamp) this.clampToRange();
          if (layout === 'pin') this.renderScroll();
          return;
        }

        this.measureSignature = signature;
        this.layout = layout;
        this.dataset.gmLayout = layout;
        this.clearScrollVars();

        if ((layout === 'fit' || layout === 'pin') && frameHeight && viewportHeight) {
          this.style.setProperty('--gm-hb-frame-height', `${frameHeight}px`);
          this.style.setProperty('--gm-hb-stage-offset', `${stageOffset}px`);
          this.style.setProperty('--gm-hb-viewport-height', `${viewportHeight}px`);
          this.stageOffset = stageOffset;
          this.viewportHeight = viewportHeight;
        }

        if (layout === 'pin') {
          this.travel = travel;
          this.sceneHeight = sceneHeight;
          this.style.setProperty('--gm-hb-travel', `${travel}px`);
          this.bindScroll();
          this.renderScroll();
        }

        if (this.pendingClamp) this.clampToRange();
      }

      clearScrollVars() {
        [
          '--gm-hb-stage-top',
          '--gm-hb-stage-offset',
          '--gm-hb-viewport-height',
          '--gm-hb-scene-height',
          '--gm-hb-travel',
          '--gm-hb-frame-height',
          '--gm-hb-stage-height',
          '--gm-hb-stage-min-height',
          '--gm-hb-scroll-distance',
          '--gm-hb-stage-pad-top',
          '--gm-hb-inner-nudge',
        ].forEach((name) => this.style.removeProperty(name));
        this.infoContent?.style.removeProperty('transform');
        this.infoContent?.style.removeProperty('padding-top');
        this.infoContent?.style.removeProperty('padding-bottom');
        this.lastProgress = null;
        this.travel = 0;
        this.stageOffset = 0;
        this.viewportHeight = 0;
        this.sceneHeight = 0;
        this.unbindScroll();
      }

      bindScroll() {
        if (this.scrollAttached) return;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.scrollAttached = true;
        this.dataset.gmScrollBound = 'true';
      }

      unbindScroll() {
        if (this.scrollFrame) {
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = 0;
        }
        if (!this.scrollAttached) return;
        window.removeEventListener('scroll', this.onScroll);
        this.scrollAttached = false;
        delete this.dataset.gmScrollBound;
      }

      onScroll() {
        if (this.scrollFrame || this.layout !== 'pin') return;
        this.scrollFrame = window.requestAnimationFrame(this.renderScroll);
      }

      renderScroll() {
        this.scrollFrame = 0;
        if (!this.isConnected || this.layout !== 'pin' || !this.infoContent) return;
        const progress = Math.round(
          Math.min(this.travel, Math.max(0, -this.getBoundingClientRect().top)) * 10
        ) / 10;
        if (progress === this.lastProgress) return;
        this.lastProgress = progress;
        this.infoContent.style.transform = progress ? `translate3d(0, ${-progress}px, 0)` : '';
      }

      clampToRange() {
        this.pendingClamp = false;
        const sectionDocTop = window.scrollY + this.getBoundingClientRect().top;
        let maxScroll = sectionDocTop;
        if (this.layout === 'pin') {
          maxScroll = sectionDocTop + this.travel;
        } else {
          maxScroll = sectionDocTop + this.offsetHeight - Math.min(window.innerHeight, this.offsetHeight);
        }
        if (window.scrollY > maxScroll + 1) {
          window.scrollTo(0, Math.max(0, maxScroll));
        }
      }
    }
  );
}
